// Last-activated view config per module, kept in localStorage so a cold load can
// start the list request before the boot views prefetch (`fetch-all`) returns.
// `hydrateBootViewCache` revalidates it against the fresh config. A dependency-free
// leaf, so auth teardown can import its clear without an import cycle.

import type { ViewTypes } from 'src/helper/shared-types';
import type { ViewDetailsType } from 'src/redux/list-view/types';

const KEY_PREFIX = 'listViewConfig:';

type StoredViewConfig = { viewId: string; config: ViewDetailsType };

export function readStoredViewConfig(
  viewType: ViewTypes,
  viewId: string,
): ViewDetailsType | undefined {
  try {
    const raw = localStorage.getItem(`${KEY_PREFIX}${viewType}`);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as StoredViewConfig;
    return stored?.viewId === viewId && stored.config ? stored.config : undefined;
  } catch {
    return undefined;
  }
}

export function writeStoredViewConfig(
  viewType: ViewTypes,
  viewId: string,
  config: ViewDetailsType,
): void {
  try {
    const stored: StoredViewConfig = { viewId, config };
    localStorage.setItem(`${KEY_PREFIX}${viewType}`, JSON.stringify(stored));
  } catch {
    // Quota or disabled storage: the next cold load just waits for fetch-all.
  }
}

export function removeStoredViewConfig(viewType: ViewTypes, viewId: string): void {
  if (readStoredViewConfig(viewType, viewId) === undefined) return;
  try {
    localStorage.removeItem(`${KEY_PREFIX}${viewType}`);
  } catch {
    // Storage unavailable: nothing was stored.
  }
}

// Auth teardown: an incoming account never starts from the outgoing user's views.
export function clearStoredViewConfigs(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(KEY_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing was stored.
  }
}
